import { AddPlus } from "@bb-ui/icons/dist/medium";
import {
  LineHorizontal,
  FullScreen,
  ExitFullScreen,
  ChevronLeft,
  ChevronRight,
  Download,
} from "@bb-ui/icons/dist/small";
import { HideOffScreen } from "@bb-ui/react-library/dist/components/HideOffScreen";
import { IconButton } from "@bb-ui/react-library/dist/components/IconButton";
import React, { ChangeEvent } from "react";
import { useTranslation, Trans } from "react-i18next";

import { debounce } from "../../utils/utils";
import { DocumentViewerOptions } from "../document-viewer/document-viewer";

export type ToolbarProps = {
  /** The total number of pages in the PDF */
  numPages: number;

  /** The number of the page that is currently displayed (1-based) */
  pageNumber: number;

  /** The scale at which the pages should be rendered */
  scale: number;

  /** Additional options that control the document viewer's behaviour */
  viewerOptions: DocumentViewerOptions;

  /** Invoked when the user wants to download the original file */
  onDownloadOriginal: () => void;

  /** Invoked when the user wants to go full screen */
  onFullScreenToggle: (goFullscreen: boolean) => void;

  /** Invoked when the user wants to change the page to the desired page */
  onPageChange: (desiredPage: number) => void;

  /** Invoked when the user wants to change the page to the desired scale */
  onScaleChange: (desiredScale: number) => void;
};

export const Toolbar: React.FC<ToolbarProps> = ({
  numPages,
  pageNumber,
  scale,
  viewerOptions,
  onDownloadOriginal,
  onFullScreenToggle,
  onPageChange,
  onScaleChange,
}: ToolbarProps) => {
  const { t } = useTranslation();

  // TODO: Account for esc-key
  /** Whether the document is rendered in full screen mode */
  const [isFullScreen, setFullScreen] = React.useState(false);

  function tryPageChange(ev: ChangeEvent<HTMLInputElement>) {
    // ev.target can be undefined in certain cases (e.g. trying to enter a page outside of the min/max range)
    if (ev.target !== undefined && ev.target.value !== undefined) {
      const desiredPage = parseInt(ev.target.value, 10);
      if (!isNaN(desiredPage) && desiredPage > 0 && desiredPage <= numPages) {
        onPageChange(desiredPage);
      }
    }
  }

  function zoomIn() {
    if (scale < 2) {
      onScaleChange(Math.round((scale + 0.2) * 10) / 10);
    }
  }

  function zoomOut() {
    if (scale > 0.4) {
      onScaleChange(Math.round((scale - 0.2) * 10) / 10);
    }
  }

  function fullScreenToggle() {
    onFullScreenToggle(!isFullScreen);
    setFullScreen(!isFullScreen);
  }

  function onFullScreenChange() {
    setFullScreen(document.fullscreenElement !== null);
  }

  React.useEffect(() => {
    // Full screen mode can be exited by hitting ESC. Listen for changes and update the state if necessary
    document.addEventListener("fullscreenchange", onFullScreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullScreenChange);
  }, []);

  const inputTextClassName = `toolbar-page-length-${
    numPages.toString().length
  }`;

  return (
    <div className="toolbar-inner" role="navigation">
      <div className="toolbar-start-container">
        <IconButton
          disabled={pageNumber === 1 ? true : false}
          aria-label={t("toolbar.prevPage")}
          onClick={() => onPageChange(pageNumber - 1)}
          title={t("toolbar.prevPage")}
        >
          <ChevronLeft />
        </IconButton>
        <span className="toolbar-page">{t("toolbar.pageIOfNPartPage")}</span>
        <span className="toolbar-pageNumber">
          <HideOffScreen>
            <label htmlFor="pageNumber">{t("toolbar.pageNumber")}</label>
          </HideOffScreen>
          <input
            id="pageNumber"
            type="text"
            className={inputTextClassName}
            pattern="[0-9]"
            value={pageNumber}
            min="1"
            max={numPages + 1}
            onChange={debounce(tryPageChange, { wait: 250 })}
          ></input>
          <span className="toolbar-pageNumber-of">
            <Trans
              i18nKey="toolbar.pageIOfNPartOfN"
              values={{ numPages }}
            ></Trans>
          </span>
        </span>
        <IconButton
          disabled={pageNumber === numPages ? true : false}
          aria-label={t("toolbar.nextPage")}
          onClick={() => onPageChange(pageNumber + 1)}
          title={t("toolbar.nextPage")}
        >
          <ChevronRight />
        </IconButton>
      </div>
      <div className="toolbar-middle-container">
        <IconButton
          aria-label={t("toolbar.zoomOut")}
          disabled={scale <= 0.4}
          title={t("toolbar.zoomOut")}
          onClick={() => zoomOut()}
        >
          <LineHorizontal />
        </IconButton>
        <IconButton
          aria-label={t("toolbar.zoomIn")}
          disabled={scale >= 2}
          title={t("toolbar.zoomIn")}
          onClick={() => zoomIn()}
        >
          <AddPlus />
        </IconButton>
        <IconButton
          aria-label={
            isFullScreen
              ? t("toolbar.exitFullScreen")
              : t("toolbar.goFullScreen")
          }
          onClick={() => fullScreenToggle()}
          title={
            isFullScreen
              ? t("toolbar.exitFullScreen")
              : t("toolbar.goFullScreen")
          }
        >
          {isFullScreen ? <ExitFullScreen /> : <FullScreen />}
        </IconButton>
      </div>
      <div className="toolbar-end-container">
        {viewerOptions.canDownload ? (
          <IconButton
            aria-label={t("toolbar.download")}
            onClick={() => onDownloadOriginal()}
            title={t("toolbar.download")}
          >
            <Download />
          </IconButton>
        ) : undefined}
      </div>
    </div>
  );
};
